package com.trevorwiebe.timesheet.admin.reporting.presentation.components

import androidx.compose.ui.draw.clip
import androidx.compose.material3.Icon
import androidx.compose.material.icons.outlined.Info
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material.icons.Icons
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.clickable
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.trevorwiebe.timesheet.admin.reporting.domain.RevenuePerMileReport
import com.trevorwiebe.timesheet.admin.reporting.domain.TruckRevenueTotal
import com.trevorwiebe.timesheet.core.domain.formatWithCommas

/** Pinned column header: Truck · Revenue · Miles · $/Mile · $/Mile without fuel surcharge. */
@Composable
fun RevenuePerMileResultsHeader() {
    Surface(color = MaterialTheme.colorScheme.background) {
        RevenueRow(
            cells = listOf("Truck", "Revenue", "Miles", "$/Mile", "No FSC"),
            bold = true,
        )
    }
}

@Composable
fun RevenuePerMileTruckRow(total: TruckRevenueTotal) {
    RevenueRow(
        cells = listOf(
            total.unitNumber,
            total.revenueFormatted,
            total.miles.formatWithCommas(),
            total.revenuePerMileFormatted,
            total.revenueExFuelPerMileFormatted,
        ),
    )
    HorizontalDivider()
}

@Composable
fun RevenuePerMileTotalsRow(report: RevenuePerMileReport) {
    RevenueRow(
        cells = listOf(
            "Total",
            report.totalRevenueFormatted,
            report.totalMiles.formatWithCommas(),
            report.totalRevenuePerMileFormatted,
            report.totalRevenueExFuelPerMileFormatted,
        ),
        bold = true,
    )
}

/** Caveats about what the totals leave out, shown above the table so they're read first. */
@Composable
fun RevenuePerMileNotices(report: RevenuePerMileReport, onReviewUnassigned: () -> Unit) {
    val unassigned = report.unassignedNotice
    val zeroRevenue = report.zeroRevenueNotice
    if (unassigned == null && zeroRevenue == null) return
    Column(
        modifier = Modifier.padding(horizontal = 12.dp, vertical = 8.dp),
        verticalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        unassigned?.let { Notice(text = it, onClick = onReviewUnassigned) }
        zeroRevenue?.let { Notice(text = it, onClick = null) }
    }
}

@Composable
private fun Notice(text: String, onClick: (() -> Unit)?) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(8.dp))
            .background(MaterialTheme.colorScheme.surfaceVariant)
            .then(if (onClick != null) Modifier.clickable(onClick = onClick) else Modifier)
            .padding(horizontal = 12.dp, vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Icon(
            imageVector = Icons.Outlined.Info,
            contentDescription = null,
            tint = MaterialTheme.colorScheme.tertiary,
            modifier = Modifier.size(18.dp),
        )
        Spacer(Modifier.width(8.dp))
        Text(
            text = text,
            fontSize = 13.sp,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            modifier = Modifier.weight(1f),
        )
        if (onClick != null) {
            Icon(
                imageVector = Icons.AutoMirrored.Filled.KeyboardArrowRight,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.size(18.dp),
            )
        }
    }
}

@Composable
private fun RevenueRow(cells: List<String>, bold: Boolean = false) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 12.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        cells.forEachIndexed { index, text ->
            Cell(text = text, bold = bold, alignEnd = index > 0, weight = if (index == 1) 1.3f else 1f)
        }
    }
}

@Composable
private fun RowScope.Cell(text: String, bold: Boolean, alignEnd: Boolean, weight: Float) {
    Text(
        text = text,
        modifier = Modifier.weight(weight),
        fontWeight = if (bold) FontWeight.Bold else null,
        fontSize = 14.sp,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis,
        textAlign = if (alignEnd) TextAlign.End else TextAlign.Start,
    )
}
